import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CONTRACT_STATUS_DEFS,
  CONTRACT_STATUS_MAP,
  type ContractStatus,
} from "@goyone/shared";
import { api, qs } from "../../lib/api";
import { useDebounced, useListState } from "../../lib/hooks";
import { date, money, plural, WORDS } from "../../lib/format";
import { Icon } from "../../components/Icon";
import { DocPreview } from "../../components/DocPreview";
import { RecordStats, type RecordStat } from "../../components/RecordStats";
import { CardPickerButton, useCardChoice } from "../../components/CardPicker";
import { ContractActions } from "./ContractActions";
import {
  ColumnPickerButton,
  useColumnChoice,
  useColumnDrag,
  type ColumnDef,
} from "../../components/ColumnPicker";
import {
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from "../../components/ColumnFilter";
import { useToast } from "../../components/Toast";
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  TableSkeleton,
} from "../../components/ui";

interface ContractRow {
  id: string;
  number: string;
  title: string;
  status: ContractStatus;
  startAt: string;
  endAt: string | null;
  value: string;
  account: { id: string; name: string } | null;
}

/** كتالوج أعمدة العقود. */
const CONTRACT_COLUMNS: readonly ColumnDef[] = [
  { key: "number", label: "الرقم", fixed: true },
  { key: "title", label: "العنوان" },
  { key: "account", label: "الحساب" },
  { key: "status", label: "الحالة" },
  { key: "value", label: "القيمة", group: "الأرقام" },
  { key: "period", label: "المدة", group: "التواريخ" },
];

/** أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في contracts.service.ts. */
const CONTRACT_FILTERS: FilterDef[] = [
  { key: "number", label: "الرقم", kind: "text", sortable: true },
  { key: "title", label: "العنوان", kind: "text" },
  { key: "account", label: "الحساب", kind: "text", noList: true },
  {
    key: "status",
    label: "الحالة",
    kind: "enum",
    options: CONTRACT_STATUS_DEFS.map((s) => ({ value: s.key, label: s.label })),
  },
  { key: "value", label: "القيمة", kind: "number", sortable: true },
];

const CONTRACT_NUMERIC = new Set(["value"]);

interface ContractPage {
  items: ContractRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export function ContractsList() {
  const queryClient = useQueryClient();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const [previewing, setPreviewing] = useState<ContractRow | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const columns = useMemo<ColumnDef[]>(() => [...CONTRACT_COLUMNS], []);
  const {
    visible: shownColumns,
    state: columnState,
    setState: setColumnState,
    reset: resetColumns,
    touched: columnsTouched,
  } = useColumnChoice("goyone.columns.contracts", columns);
  const colDrag = useColumnDrag(columnState, setColumnState);
  const toast = useToast();
  const [search, setSearch] = useState(state.q ?? "");
  const debounced = useDebounced(search);
  /*
   * ملف العميل يرسل «إضافة عقد» إلى هنا بـ`?new=1&accountId=` — فيفتح
   * النموذج والعميلُ منتقًى، لا صفحةَ قوائم يُبحث فيها عن زرّ.
   */
  const [params] = useSearchParams();
  const [creating, setCreating] = useState(params.get("new") === "1");
  const presetAccountId = params.get("accountId") ?? "";

  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ["contracts", query],
    queryFn: () => api<ContractPage>(`/sales/contracts${qs(query)}`),
  });

  const now = Date.now();

  const bulkDelete = useMutation({
    mutationFn: async (ids: string[]) => {
      let ok = 0;
      for (const id of ids) {
        try {
          await api(`/sales/contracts/${id}`, { method: "DELETE" });
          ok += 1;
        } catch {
          /* عقدٌ سارٍ قد لا يُحذف — يُتخطّى ويُحصى الباقي. */
        }
      }
      return ok;
    },
    onSuccess: (ok) => {
      toast(`حُذف ${ok} من ${picked.size}`);
      setPicked(new Set());
      void queryClient.invalidateQueries({ queryKey: ["contracts"] });
      void queryClient.invalidateQueries({ queryKey: ["account"] });
      void queryClient.invalidateQueries({ queryKey: ["quotations"] });
      void queryClient.invalidateQueries({ queryKey: ["lead-deals"] });
    },
  });

  const pageIds = (list.data?.items ?? []).map((c) => c.id);

  const cardChoice = useCardChoice("goyone.stats.contracts", [
    "count",
    "value",
    "active",
    "expiring",
  ]);
  const statItems = list.data ? contractCards(list.data) : [];
  const statChoices = statItems.map(({ key, label, group }) => ({
    key,
    label,
    group,
  }));

  return (
    <div className="page">
      <PageHead
        title="العقود"
        subtitle={
          list.data ? plural(list.data.total, WORDS.contract) : undefined
        }
        actions={
          /* من جاء من ملف العميل يعود إليه بزرٍّ صريح — لا بحثَ في السجلّ. */
          presetAccountId ? (
            <Link to={`/crm/accounts/${presetAccountId}`} className="btn">
              <Icon name="arrowLeft" size={15} />
              ملف العميل
            </Link>
          ) : undefined
        }
      />

      {list.data && (
        <RecordStats
          items={statItems}
          storageKey="goyone.stats.contracts"
          defaults={["count", "value", "active", "expiring"]}
          external={cardChoice}
        />
      )}

      {picked.size > 0 && (
        <div className="bulk-bar">
          <span className="strong">{picked.size} محدَّد</span>
          <button className="btn btn-sm" onClick={() => setPicked(new Set())}>
            إلغاء التحديد
          </button>
          <button
            className="btn btn-sm btn-danger"
            style={{ marginInlineStart: "auto" }}
            disabled={bulkDelete.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `حذف ${picked.size} عقدًا؟ العقود السارية قد تُتخطّى.`,
                )
              )
                bulkDelete.mutate([...picked]);
            }}
          >
            <Icon name="trash" size={14} />
            حذف المحدَّد
          </button>
        </div>
      )}

      {/* ترتيب الشريط كقوائم إدارة العملاء: بحثٌ فمرشّحات، والاختيارات والإضافة في طرفه. */}
      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث برقم العقد أو عنوانه"
        />

        <select
          className="select"
          style={{ width: "auto" }}
          value={state.status ?? ""}
          onChange={(e) => patch({ status: e.target.value || undefined })}
          aria-label="الحالة"
        >
          <option value="">كل الحالات</option>
          {CONTRACT_STATUS_DEFS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>

        <span
          className="row"
          style={{ marginInlineStart: "auto", gap: "var(--sp-2)" }}
        >
          <FilterButton
            defs={CONTRACT_FILTERS}
            filters={filters}
            onChange={setFilters}
          />
          <ColumnPickerButton
            columns={columns}
            state={columnState}
            onChange={setColumnState}
            onReset={resetColumns}
            touched={columnsTouched}
          />
          <CardPickerButton
            choices={statChoices}
            chosen={cardChoice.chosen}
            onChange={cardChoice.setChosen}
            onReset={cardChoice.reset}
          />
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setCreating(true)}
          >
            <Icon name="plus" size={14} />
            عقد جديد
          </button>
        </span>
      </div>

      <FilterChips
        defs={CONTRACT_FILTERS}
        filters={filters}
        onChange={setFilters}
      />

      <FilterScope
        defs={CONTRACT_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/sales/contracts/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                <th className="col-act" />
                {shownColumns.map((c) => (
                  <th
                    key={c.key}
                    {...colDrag.thProps(
                      c,
                      CONTRACT_NUMERIC.has(c.key) ? "col-num" : undefined,
                    )}
                  >
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
                <th className="col-pick">
                  <input
                    type="checkbox"
                    aria-label="تحديد كل الصفحة"
                    checked={
                      pageIds.length > 0 &&
                      pageIds.every((id) => picked.has(id))
                    }
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          pageIds.some((id) => picked.has(id)) &&
                          !pageIds.every((id) => picked.has(id));
                    }}
                    onChange={(e) => {
                      const next = new Set(picked);
                      if (e.target.checked)
                        pageIds.forEach((id) => next.add(id));
                      else pageIds.forEach((id) => next.delete(id));
                      setPicked(next);
                    }}
                  />
                </th>
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={shownColumns.length + 2} />}

            {list.data && (
              <tbody>
                {list.data.items.map((c) => {
                  const ending =
                    c.status === "ACTIVE" &&
                    c.endAt &&
                    new Date(c.endAt).getTime() - now < 30 * 86_400_000;
                  return (
                    <tr key={c.id}>
                      <td className="col-act">
                        <ContractActions
                          contract={c}
                          onPreview={() => setPreviewing(c)}
                          onChanged={() => void list.refetch()}
                        />
                      </td>
                      {shownColumns.map((col) => (
                        <ContractCell
                          key={col.key}
                          column={col}
                          contract={c}
                          ending={Boolean(ending)}
                        />
                      ))}
                      <td className="col-pick">
                        <input
                          type="checkbox"
                          aria-label={`تحديد العقد ${c.number}`}
                          checked={picked.has(c.id)}
                          onChange={(e) => {
                            const next = new Set(picked);
                            if (e.target.checked) next.add(c.id);
                            else next.delete(c.id);
                            setPicked(next);
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        )}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا توجد عقود"
            text="سجّل العقود لتتبّع مددها ودفعاتها."
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
          />
        )}
      </div>
      </FilterScope>

      {creating && (
        <ContractModal
          initialAccountId={presetAccountId}
          onClose={() => setCreating(false)}
          onSaved={() => {
            void queryClient.invalidateQueries({ queryKey: ["contracts"] });
            void queryClient.invalidateQueries({ queryKey: ["account"] });
            void queryClient.invalidateQueries({ queryKey: ["quotations"] });
            void queryClient.invalidateQueries({ queryKey: ["lead-deals"] });
            setCreating(false);
          }}
        />
      )}

      {previewing && (
        <DocPreview
          path={`/print/contract/${previewing.id}`}
          title={`عقد ${previewing.number}`}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}

/** خليّةٌ واحدة، يُختار شكلها بمفتاح العمود لا بموضعه. */
function ContractCell({
  column,
  contract: c,
  ending,
}: {
  column: ColumnDef;
  contract: ContractRow;
  ending: boolean;
}) {
  switch (column.key) {
    case "number":
      return <td className="cell-strong num">{c.number}</td>;

    case "title":
      return <td>{c.title}</td>;

    case "account":
      return (
        <td>
          {c.account ? (
            <Link
              to={`/crm/accounts/${c.account.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              {c.account.name}
            </Link>
          ) : (
            "—"
          )}
        </td>
      );

    case "status":
      return (
        <td>
          <Badge kind={CONTRACT_STATUS_MAP[c.status]?.kind}>
            {CONTRACT_STATUS_MAP[c.status]?.label}
          </Badge>
        </td>
      );

    case "value":
      return <td className="num">{money(c.value)}</td>;

    case "period":
      /* المشارف على الانتهاء بلونٍ محذّر: هو ما يُجدَّد قبل فواته. */
      return (
        <td className="cell-mute">
          {date(c.startAt)}
          {c.endAt && (
            <>
              {" — "}
              <span style={{ color: ending ? "var(--warning)" : undefined }}>
                {date(c.endAt)}
              </span>
            </>
          )}
        </td>
      );

    default:
      return <td>—</td>;
  }
}

/**
 * بطاقات العقود — من الصفحة المعروضة، كبطاقات العروض.
 *
 * «تنتهي خلال ٦٠ يومًا» هي ما يُتصرَّف فيه: عقدٌ يقترب انتهاؤه يُجدَّد أو
 * يُفقد، وعدُّه هنا يسبق أن يفوت أوانه.
 */
function contractCards(page: ContractPage): RecordStat[] {
  const items = page.items;
  const sum = (rows: ContractRow[]) =>
    rows.reduce((n, c) => n + Number(c.value ?? 0), 0);
  const active = items.filter((c) => c.status === "ACTIVE");
  const soon = active.filter((c) => {
    if (!c.endAt) return false;
    const days = (new Date(c.endAt).getTime() - Date.now()) / 86_400_000;
    return days >= 0 && days <= 60;
  });

  return [
    {
      key: "count",
      label: "عقود (إجمالًا)",
      group: "العدد",
      icon: "book",
      color: "#3672EA",
      value: page.total,
      numeric: true,
      note: `${items.length} في هذه الصفحة`,
    },
    {
      key: "value",
      label: "قيمة هذه الصفحة",
      group: "القيمة",
      icon: "wallet",
      color: "#1D46A6",
      value: money(sum(items)),
      numeric: true,
    },
    {
      key: "active",
      label: "سارية",
      group: "العدد",
      icon: "check",
      color: "#51A521",
      value: active.length,
      numeric: true,
      note: money(sum(active)),
    },
    {
      key: "expiring",
      label: "تنتهي خلال ٦٠ يومًا",
      group: "المتابعة",
      icon: "alert",
      color: soon.length ? "#BF930B" : "#9BA3AE",
      value: soon.length,
      numeric: true,
      note: soon.length ? "جدّدها قبل فواتها" : "لا شيء قريب",
    },
    {
      key: "avg",
      label: "متوسط قيمة العقد",
      group: "القيمة",
      icon: "chartBar",
      color: "#8791A1",
      value: items.length ? money(Math.round(sum(items) / items.length)) : "—",
      numeric: true,
    },
  ];
}

function ContractModal({
  initialAccountId = "",
  onClose,
  onSaved,
}: {
  initialAccountId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [accountId, setAccountId] = useState(initialAccountId);
  /*
   * العقد يُبنى على عرض سعرٍ يُختار — لا عقدَ من فراغ.
   *
   * الاختيار إلزاميّ: تُعرض عروض العميل المقبولة التي لا عقد لها،
   * والإنشاء ينسخ من العرض قيمتَه وبنودَه، فلا يفترق العقد عمّا وافق
   * عليه العميل. والخادم يحرس القاعدة نفسها من أي بابٍ جاء الطلب.
   */
  const [quotationId, setQuotationId] = useState("");
  const [title, setTitle] = useState("");
  const [startAt, setStartAt] = useState(new Date().toISOString().slice(0, 10));
  const [endAt, setEndAt] = useState("");

  const accounts = useQuery({
    queryKey: ["accounts", "options"],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string }> }>(
        `/crm/accounts${qs({ pageSize: 100, sort: "name", order: "asc" })}`,
      ),
  });

  /* ملف العميل يحمل عروضه بحال كلٍّ منها (عقده وفاتورته) — منه الأهلية. */
  const profile = useQuery({
    queryKey: ["account", accountId],
    queryFn: () => api<Record<string, any>>(`/crm/accounts/${accountId}`),
    enabled: Boolean(accountId),
  });
  const eligible = (
    (profile.data?.quotations ?? []) as Array<Record<string, any>>
  ).filter((q) => q.status === "ACCEPTED" && !q.contracts?.[0]);
  const picked = eligible.find((q) => q.id === quotationId) ?? null;

  const create = useMutation({
    mutationFn: () =>
      api("/sales/contracts/from-quotation", {
        method: "POST",
        body: {
          quotationId,
          title: title.trim() || undefined,
          startAt: new Date(startAt).toISOString(),
          endAt: endAt ? new Date(endAt).toISOString() : undefined,
        },
      }),
    onSuccess: onSaved,
  });

  const valid = accountId && quotationId && startAt;

  return (
    <Modal
      title="عقد جديد"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => create.mutate()}
            disabled={!valid || create.isPending}
          >
            {create.isPending ? "جارٍ الحفظ" : "حفظ"}
          </button>
        </>
      }
    >
      {create.isError && (
        <div className="alert alert-danger mb-4">
          {create.error instanceof Error
            ? create.error.message
            : "تعذّر الحفظ."}
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="c-account">
            الحساب
          </label>
          <select
            id="c-account"
            className="select"
            value={accountId}
            onChange={(e) => {
              setAccountId(e.target.value);
              /* عميلٌ آخر عروضٌ أخرى — الاختيار السابق لا يصحّ. */
              setQuotationId("");
            }}
          >
            <option value="">اختر حسابًا</option>
            {accounts.data?.items.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="label req" htmlFor="c-quotation">
            عرض السعر الذي يُبنى عليه العقد
          </label>
          <select
            id="c-quotation"
            className="select"
            value={quotationId}
            onChange={(e) => setQuotationId(e.target.value)}
            disabled={!accountId || eligible.length === 0}
          >
            <option value="">
              {!accountId
                ? "اختر حسابًا أولًا"
                : profile.isPending
                  ? "جارٍ جلب العروض…"
                  : eligible.length === 0
                    ? "لا عرض مقبولًا بلا عقد"
                    : "اختر عرض السعر"}
            </option>
            {eligible.map((q) => (
              <option key={q.id} value={q.id}>
                {q.number}
                {q.title ? ` — ${q.title}` : ""} ({money(q.total)})
              </option>
            ))}
          </select>
          {accountId && !profile.isPending && eligible.length === 0 && (
            <p className="tiny mute">
              العقد يوثّق عرضًا وافق عليه العميل — أرسل عرض سعرٍ ونل موافقته
              أولًا.
            </p>
          )}
          {picked && (
            <p className="tiny mute">
              تُنسخ من العرض قيمتُه{" "}
              <span className="num">{money(picked.total)}</span> وبنودُه وشروطُه
              — وتقبل التعديل داخل العقد قبل توقيعه وتفعيله.
            </p>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="c-title">
            عنوان العقد
          </label>
          <input
            id="c-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={picked?.title ?? "يُؤخذ من عنوان العرض إن تُرك فارغًا"}
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="c-start">
            تاريخ البدء
          </label>
          <input
            id="c-start"
            className="input"
            type="date"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="c-end">
            تاريخ الانتهاء
          </label>
          <input
            id="c-end"
            className="input"
            type="date"
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
